import React, { useCallback, useEffect, useState } from 'react';
import './HoursAdjustment.css';
import DuplicatesModal from './DuplicatesModal';
import api, { userService } from '../services/api';

const HoursAdjustment = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editedHours, setEditedHours] = useState({});
  const [workdaysOnly, setWorkdaysOnly] = useState(true);
  const [bulkWeekdayHours, setBulkWeekdayHours] = useState('');
  const [bulkWeekendHours, setBulkWeekendHours] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  
  // Duplicates modal state
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const data = await userService.getAll();
      // Show all users including admins
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadHours = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hours-adjustment`, {
        params: { user_id: selectedUser, month: selectedMonth, workdays_only: workdaysOnly }
      });
      setHours(data);
    } catch (error) {
      console.error('Error loading hours:', error);
      setHours([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, workdaysOnly]);

  useEffect(() => {
    loadUsers();
    // Set current month as default
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(currentMonth);
  }, [loadUsers]);

  useEffect(() => {
    if (selectedUser && selectedMonth) {
      loadHours();
    }
  }, [loadHours, selectedMonth, selectedUser]);


  const handleEdit = (record) => {
    setEditingId(record.id);
    setEditedHours({
      hours: record.hours,
      note: record.note || '',
      shift: record.shift || 'work'
    });
  };

  const handleSave = async (recordId) => {
    try {
      await api.put(`/hours-adjustment/${recordId}`, editedHours);
      alert('Hodiny boli úspešne upravené!');
      setEditingId(null);
      loadHours();
    } catch (error) {
      console.error('Error saving hours:', error);
      alert('Chyba pri ukladaní zmien!');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedHours({});
  };

  const handleBulkUpdate = async () => {
    if (!bulkWeekdayHours && !bulkWeekendHours) {
      alert('Zadajte aspoň jednu hodnotu (pracovné dni alebo víkend)');
      return;
    }

    const confirmMessage = [];
    if (bulkWeekdayHours) {
      confirmMessage.push(`Pracovné dni: ${bulkWeekdayHours}h`);
    }
    if (bulkWeekendHours) {
      confirmMessage.push(`Víkend: ${bulkWeekendHours}h`);
    }

    if (!window.confirm(`Naozaj chcete hromadne upraviť hodiny?\n${confirmMessage.join('\n')}\nPoznámka: ${bulkNote || 'žiadna'}`)) {
      return;
    }

    setLoading(true);
    try {
      const { data: result } = await api.post('/hours-adjustment/bulk', {
        user_id: selectedUser,
        month: selectedMonth,
        weekday_hours: bulkWeekdayHours ? parseFloat(bulkWeekdayHours) : undefined,
        weekend_hours: bulkWeekendHours ? parseFloat(bulkWeekendHours) : undefined,
        note: bulkNote,
        workdays_only: workdaysOnly
      });

      if (result) {
        alert(result.message || 'Hromadná úprava bola úspešná!');
        setBulkWeekdayHours('');
        setBulkWeekendHours('');
        setBulkNote('');
        loadHours();
      } else {
        alert(`Chyba: ${result.error || 'Nepodarilo sa vykonať hromadnú úpravu'}`);
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      alert('Chyba pri hromadnej úprave hodín!');
    } finally {
      setLoading(false);
    }
  };

  const handleFixZeroHours = async () => {
    if (!selectedUser) {
      alert('Prosím vyberte používateľa');
      return;
    }

    const userName = users.find(u => u.id === parseInt(selectedUser))?.full_name || 'N/A';
    
    if (!window.confirm(`Naozaj chcete upraviť všetky dni s 0 hodinami na "Doma" pre používateľa ${userName}?\n\nTáto akcia sa vykoná pre celý rok.`)) {
      return;
    }

    setLoading(true);
    try {
      const { data: result } = await api.post('/hours-adjustment/fix-zero-hours', {
        user_id: selectedUser
      });
      alert(result.message || 'Nulové dni boli úspešne opravené!');
      loadHours();
    } catch (error) {
      console.error('Error fixing zero hours:', error);
      alert('Chyba pri oprave nulových hodín!');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDuplicates = async () => {
    if (!selectedUser) {
      alert('Prosím vyberte používateľa');
      return;
    }

    if (!selectedMonth) {
      alert('Prosím vyberte mesiac');
      return;
    }

    setLoadingDuplicates(true);
    try {
      const { data } = await api.get(`/attendance/duplicates`, {
        params: { user_id: selectedUser, month: selectedMonth }
      });
      setDuplicates(data.duplicates || []);
      setShowDuplicatesModal(true);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      alert('Chyba pri načítavaní duplicít!');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleRefreshDuplicates = () => {
    // Refresh duplicates after deletion
    handleCheckDuplicates();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sk-SK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const getDayType = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getTotalHours = () => {
    return hours.reduce((sum, record) => sum + parseFloat(record.hours || 0), 0).toFixed(2);
  };

  const getShiftName = (shift) => {
    const shiftNames = {
      'work': '💼 Práca',
      'morning': '🌅 Ranná',
      'afternoon': '☀️ Denná',
      'night': '🌙 Nočná',
      'home': '🏠 Domov',
      'vacation': '🏖️ Dovolenka',
      'sick': '🤒 PN',
      'holiday': '🎉 Sviatok'
    };
    return shiftNames[shift] || shift;
  };

  return (
    <div className="hours-adjustment-container">
      <div className="hours-adjustment-header">
        <h1>⏰ Úprava odpracovaných hodín</h1>
        <p className="subtitle">Úprava hodín pre chorých, neskorý príchod alebo skorý odchod</p>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="user-select">👤 Používateľ:</label>
          <select
            id="user-select"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="filter-select"
          >
            <option value="">-- Vyberte používateľa --</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="month-select">📅 Mesiac:</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="filter-select"
          >
            <option value="">-- Vyberte mesiac --</option>
            {Array.from({ length: 12 }, (_, i) => {
              const currentYear = new Date().getFullYear();
              const month = String(i + 1).padStart(2, '0');
              const value = `${currentYear}-${month}`;
              const monthNames = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 
                                 'Júl', 'August', 'September', 'Október', 'November', 'December'];
              return (
                <option key={value} value={value}>
                  {monthNames[i]} {currentYear}
                </option>
              );
            })}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="workdays-filter">
            <input
              type="checkbox"
              id="workdays-filter"
              checked={workdaysOnly}
              onChange={(e) => setWorkdaysOnly(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Iba dni, kedy užívateľ pracoval
          </label>
        </div>
      </div>

      {selectedUser && selectedMonth && (
        <div className="bulk-adjustment-section">
          <h3>🔄 Hromadná úprava hodín</h3>
          <p className="bulk-subtitle">Nastavte hodiny pre všetky pracovné dni alebo víkendy v mesiaci naraz</p>
          
          <div className="bulk-inputs">
            <div className="bulk-input-group">
              <label htmlFor="bulk-weekday">☀️ Pracovné dni (Po-Pia):</label>
              <input
                type="number"
                id="bulk-weekday"
                step="0.5"
                min="0"
                max="24"
                value={bulkWeekdayHours}
                onChange={(e) => setBulkWeekdayHours(e.target.value)}
                placeholder="napr. 8"
                className="bulk-input"
              />
              <span className="input-unit">hodín</span>
            </div>

            <div className="bulk-input-group">
              <label htmlFor="bulk-weekend">🌙 Víkend (So-Ne):</label>
              <input
                type="number"
                id="bulk-weekend"
                step="0.5"
                min="0"
                max="24"
                value={bulkWeekendHours}
                onChange={(e) => setBulkWeekendHours(e.target.value)}
                placeholder="napr. 0"
                className="bulk-input"
              />
              <span className="input-unit">hodín</span>
            </div>

            <div className="bulk-input-group full-width">
              <label htmlFor="bulk-note">📝 Poznámka:</label>
              <input
                type="text"
                id="bulk-note"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder="Dôvod hromadnej úpravy..."
                className="bulk-input"
              />
            </div>
          </div>

          <div className="bulk-buttons">
            <button 
              onClick={handleBulkUpdate}
              className="btn-bulk-update"
              disabled={loading || (!bulkWeekdayHours && !bulkWeekendHours)}
            >
              ⚡ Aplikovať hromadnú úpravu
            </button>
            
            <button 
              onClick={handleFixZeroHours}
              className="btn-fix-zero-hours"
              disabled={loading}
            >
              🏠 Opraviť nulové dni na "Doma"
            </button>

            <button 
              onClick={handleCheckDuplicates}
              className="btn-check-duplicates"
              disabled={loadingDuplicates}
            >
              {loadingDuplicates ? '⏳ Načítavam...' : '🔍 Skontrolovať duplicity'}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam dáta...</p>
        </div>
      )}

      {!loading && selectedUser && selectedMonth && hours.length === 0 && (
        <div className="no-data">
          <p>📭 Žiadne záznamy pre vybraný mesiac</p>
        </div>
      )}

      {!loading && hours.length > 0 && (
        <>
          <div className="summary-bar">
            <div className="summary-item">
              <span className="summary-label">👤 Používateľ:</span>
              <span className="summary-value">{hours[0]?.user_name || 'N/A'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Celkový počet dní:</span>
              <span className="summary-value">{hours.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Celkový počet hodín:</span>
              <span className="summary-value">{getTotalHours()} h</span>
            </div>
          </div>

          <div className="table-container">
            <table className="hours-table">
              <thead>
                <tr>
                  <th>Dátum</th>
                  <th>Deň</th>
                  <th>Hodiny</th>
                  <th>Smena</th>
                  <th>Poznámka</th>
                  <th>Akcie</th>
                </tr>
              </thead>
              <tbody>
                {hours.map(record => (
                  <tr key={record.id} className={`row-${getDayType(record.date)}`}>
                    <td>{formatDate(record.date)}</td>
                    <td>
                      <span className={`day-badge ${getDayType(record.date)}`}>
                        {getDayType(record.date) === 'weekend' ? '🌙 Víkend' : '☀️ Pracovný'}
                      </span>
                    </td>
                    <td>
                      {editingId === record.id ? (
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="24"
                          value={editedHours.hours}
                          onChange={(e) => setEditedHours({
                            ...editedHours,
                            hours: e.target.value
                          })}
                          className="edit-input hours-input"
                        />
                      ) : (
                        <span className="hours-value">{record.hours} h</span>
                      )}
                    </td>
                    <td>
                      {editingId === record.id ? (
                        <select
                          value={editedHours.shift}
                          onChange={(e) => setEditedHours({
                            ...editedHours,
                            shift: e.target.value
                          })}
                          className="edit-input shift-select"
                        >
                          <option value="work">💼 Práca</option>
                          <option value="morning">🌅 Ranná</option>
                          <option value="afternoon">☀️ Denná</option>
                          <option value="night">🌙 Nočná</option>
                          <option value="home">🏠 Doma</option>
                        </select>
                      ) : (
                        <span className="shift-value">{getShiftName(record.shift)}</span>
                      )}
                    </td>
                    <td>
                      {editingId === record.id ? (
                        <input
                          type="text"
                          value={editedHours.note}
                          onChange={(e) => setEditedHours({
                            ...editedHours,
                            note: e.target.value
                          })}
                          placeholder="Dôvod úpravy..."
                          className="edit-input note-input"
                        />
                      ) : (
                        <span className="note-text">{record.note || '-'}</span>
                      )}
                    </td>
                    <td>
                      {editingId === record.id ? (
                        <div className="action-buttons">
                          <button
                            onClick={() => handleSave(record.id)}
                            className="btn-save"
                            title="Uložiť"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancel}
                            className="btn-cancel"
                            title="Zrušiť"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(record)}
                          className="btn-edit"
                          title="Upraviť"
                        >
                          ✏️ Upraviť
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Duplicates Modal */}
      <DuplicatesModal
        isOpen={showDuplicatesModal}
        onClose={() => setShowDuplicatesModal(false)}
        duplicates={duplicates}
        onRefresh={handleRefreshDuplicates}
      />
    </div>
  );
};

export default HoursAdjustment;
